import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const markets = [
  {
    destination: 'Ethiopia',
    flag: '\uD83C\uDDEA\uD83C\uDDF9',
    region: 'East Africa',
    description:
      'One of the fastest-growing economies in Africa. We supply electric vehicles, construction equipment, and project materials for major Ethiopian developments.',
    focus: ['EV Vehicles', 'Machinery', 'Construction Supply'],
  },
  {
    destination: 'Djibouti',
    flag: '\uD83C\uDDE9\uD83C\uDDEF',
    region: 'Horn of Africa',
    description:
      'A key strategic port and gateway to East Africa. ABUMI supplies vehicles and heavy machinery to Djibouti\'s growing infrastructure and logistics sector.',
    focus: ['Port Logistics', 'Heavy Machinery', 'Fleet Vehicles'],
  },
  {
    destination: 'Dubai',
    flag: '\uD83C\uDDE6\uD83C\uDDEA',
    region: 'Middle East',
    description:
      'The global trade and luxury development hub. We source high-spec vehicles, premium FF&E, and construction supplies for Dubai\'s ambitious development pipeline.',
    focus: ['Luxury Vehicles', 'Hotel FF&E', 'Development Supply'],
  },
];

export default function Markets() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="markets" className="py-32 bg-charcoal relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-px h-full bg-charcoal-border opacity-40" />
        <div className="absolute top-0 left-2/4 w-px h-full bg-charcoal-border opacity-40" />
        <div className="absolute top-0 left-3/4 w-px h-full bg-charcoal-border opacity-40" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        {/* Header */}
        <div
          ref={ref}
          className={`text-center mb-20 transition-all duration-800 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="text-gold text-[10px] font-semibold tracking-[0.35em] uppercase mb-5 block">
            Our Markets
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-light text-white leading-tight mb-4">
            China to the World
          </h2>
          <div className="flex justify-center">
            <div className="section-divider" />
          </div>
          <p className="text-[#7A7A7A] text-sm mt-6 max-w-xl mx-auto leading-relaxed tracking-wide">
            Strategically focused on high-growth markets across East Africa and the Middle East.
          </p>
        </div>

        {/* Flow: China → Destinations */}
        <div
          className={`flex flex-col items-center mb-12 transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="inline-flex items-center gap-4 border border-gold px-8 py-4">
            <span className="text-2xl">&#127464;&#127475;</span>
            <div>
              <div className="text-[10px] text-gold tracking-[0.25em] uppercase font-medium">Origin</div>
              <div className="font-heading text-2xl text-white font-light">China</div>
            </div>
          </div>
          <div className="flex flex-col items-center mt-3">
            <div className="w-px h-8 bg-gradient-to-b from-gold/80 to-gold/20" />
            <i className="ri-arrow-down-s-line text-gold -mt-1" />
          </div>
        </div>

        {/* Market Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {markets.map((market, i) => (
            <div
              key={market.destination}
              className={`border border-charcoal-border hover:border-gold/50 p-8 group transition-all duration-700 cursor-default ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${350 + i * 130}ms` }}
            >
              <div className="flex items-center gap-4 mb-6">
                <span className="text-4xl">{market.flag}</span>
                <div>
                  <div className="text-[9px] text-gold tracking-[0.25em] uppercase font-medium mb-0.5">{market.region}</div>
                  <h3 className="font-heading text-2xl text-white font-light group-hover:text-gold transition-colors duration-300">
                    {market.destination}
                  </h3>
                </div>
              </div>

              <div className="w-8 h-px bg-gold/40 mb-5" />

              <p className="text-[#7A7A7A] text-sm leading-relaxed tracking-wide mb-7">
                {market.description}
              </p>

              <div className="flex flex-wrap gap-2">
                {market.focus.map((f) => (
                  <span
                    key={f}
                    className="text-[10px] text-charcoal-text border border-charcoal-border px-3 py-1 tracking-wider group-hover:border-gold/30 group-hover:text-gold/70 transition-all duration-300"
                  >
                    {f}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
